import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { usePendingTaskStore } from '@/core/stores/pendingTaskStore';
import { CATEGORY_LABEL, type NoteCategory } from '@/core/models/note';

/**
 * Shows in-flight processing tasks above the note list so the user can:
 *   - see what's still being processed after navigating back from /tasks/:id
 *   - tap to jump back into the live processing view
 *   - notice failures (with a back-link to the page where retry lives)
 *
 * Reads from pendingTaskStore (driven by taskDriver). Hides completed tasks
 * since they're already represented as a Note in the main list.
 */
export function PendingTasksStrip() {
  const tasks = usePendingTaskStore((s) => s.tasks);
  const items = Object.values(tasks).filter((t) => t.status !== 'completed');
  if (items.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="px-1 text-xs font-medium text-fg-muted">处理中</div>
      <AnimatePresence initial={false}>
        {items.map((t) => {
          const failed = t.status === 'failed';
          const progress = normalizeProgress(t.progress);
          return (
            <motion.div
              key={t.task_id}
              layout
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
            >
              <Link
                to={`/tasks/${t.task_id}`}
                className={
                  'group relative block overflow-hidden rounded-2xl border bg-bg-elevated/80 p-3 backdrop-blur transition-colors ' +
                  (failed
                    ? 'border-danger/40 hover:border-danger/60'
                    : 'border-border hover:border-accent/40')
                }
              >
                <div className="flex items-center gap-2 text-xs text-fg-subtle">
                  {t.category && (
                    <span className="rounded-md bg-bg px-2 py-0.5">
                      {CATEGORY_LABEL[t.category as NoteCategory] ?? t.category}
                    </span>
                  )}
                  <span>
                    {failed ? '处理失败' : labelStage(t.stage) || '准备中'}
                  </span>
                  {!failed && (
                    <span className="ml-auto text-[11px] tabular-nums">
                      {Math.round(progress)}%
                    </span>
                  )}
                </div>
                <div className="mt-1 line-clamp-1 text-sm font-medium">
                  {t.title || '正在生成标题…'}
                </div>
                <div className="mt-2 h-0.5 w-full overflow-hidden rounded-full bg-border/40">
                  {failed ? (
                    <div className="h-full w-full bg-danger/70" />
                  ) : (
                    <motion.div
                      key={progress}
                      className="h-full bg-gradient-to-r from-accent to-accent-2"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ ease: 'easeOut', duration: 0.4 }}
                    />
                  )}
                </div>
              </Link>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

function normalizeProgress(raw?: number): number {
  if (raw === undefined || Number.isNaN(raw)) return 2;
  // Backend task progress is 0..100; tolerate legacy/local 0..1 fractions.
  const pct = raw <= 1 ? raw * 100 : raw;
  return Math.min(100, Math.max(2, pct));
}

function labelStage(stage?: string): string {
  if (!stage) return '';
  const map: Record<string, string> = {
    pending: '准备中',
    uploading: '上传中',
    transcribing: '转写中',
    draft_building: '起草中',
    draft_ready: '草稿就绪',
    enrich_building: '深化中',
    llm_processing: '处理中',
    completed: '已完成',
    failed: '失败',
  };
  return map[stage] ?? stage;
}
